import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";

import type { CreateOtcQuotePayload, OtcAmountAnchor, OtcQuote } from "../types/cryptoSwaps.types";

export type ConverterFieldSource = "typed" | "quoted" | "pending" | "empty";

export interface ConverterField {
  value: string;
  source: ConverterFieldSource;
}

export interface ConverterState {
  anchor: OtcAmountAnchor;
  amount: string;
  /** The two legs as the fields show them; a sale maps its COP field to the proceeds paid to the key. */
  quote: Pick<OtcQuote, "send" | "receive"> | null;
  isPricing: boolean;
}

/** The anchor shows what was typed and is never overwritten; the other side shows the quote's answer. */
export function converterField(side: OtcAmountAnchor, state: ConverterState): ConverterField {
  if (side === state.anchor) return { value: state.amount, source: state.amount ? "typed" : "empty" };
  if (state.isPricing) return { value: "", source: "pending" };
  const quoted = state.quote ? (side === "send" ? state.quote.send.amount : state.quote.receive.amount) : "";
  return quoted ? { value: quoted, source: "quoted" } : { value: "", source: "empty" };
}

export function quoteAmountPayload(
  anchor: OtcAmountAnchor,
  amount: string,
): Pick<CreateOtcQuotePayload, "send_amount" | "receive_amount"> {
  const value = amount.trim();
  return anchor === "send" ? { send_amount: value } : { receive_amount: value };
}

/** Pricing by COP rounds the USDT down, so the real debit can land a few pesos under what was typed. */
export function debitDiffersFromTyped(
  anchor: OtcAmountAnchor,
  typed: string,
  quote: Pick<OtcQuote, "send"> | null,
): boolean {
  if (anchor !== "send" || !quote) return false;
  const value = typed.trim();
  const debit = quote.send.amount;
  if (!isDecimalString(value) || !isDecimalString(debit)) return false;
  return compareDecimalStrings(value, debit) !== 0;
}
